/**
 * Client for triggering NAR RPA (queue-first with direct API fallback).
 * @module narRpaTrigger
 */

import { FRONT_END_PRIORITY_VALUE_FOR_RABBITMQ_QUEUE } from "../../constants/stringLiterals";
import { RPATaskRequest, RPATaskResponse } from "../../data-model/rpaTaskQueue";
import { API_ENDPOINTS, RPA_PATHS_ONLY_FOR_QUEUE } from "../../enums/apiUrls";
import { PortalIdForRabbitMq } from "../../enums/portalIdsForRabbitMq";
import { logDataToConsole } from "../../utils/customLogger";
import { getBearerToken } from "./bearerToken";
import { sendRpaRequestsToRabbitMq } from "./sendRpaRequestsToRabbitMq";

/** Minimal identifiers required to enqueue or run NAR RPA for an order. */
export interface NarRpaTriggerPayload {
  order_id: string;
  org_id: string;
}

/**
 * Sends a direct API request to trigger NAR RPA.
 */
const sendDirectApiRequest = async (
  payload: NarRpaTriggerPayload,
  token: string,
): Promise<RPATaskResponse> => {
  const response = await fetch(API_ENDPOINTS.NAR_RPA_TRIGGER, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      `Request failed with status ${response.status}: ${errorData.message || response.statusText}`,
    );
  }

  await response.json();
  return {
    success: true,
    message: "NAR RPA trigger initiated successfully",
  };
};

/**
 * Builds the RPA task request payload for the queue.
 */
const buildRpaTaskRequest = (
  payload: NarRpaTriggerPayload,
  token: string,
): RPATaskRequest => ({
  payload,
  org_id: payload.org_id,
  portal_id: PortalIdForRabbitMq.HEALTHFIRST_NAR,
  priority: FRONT_END_PRIORITY_VALUE_FOR_RABBITMQ_QUEUE,
  identifier: payload.order_id,
  order_id: payload.order_id,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
});

/**
 * Triggers NAR RPA via RPA queue with direct API fallback.
 * First attempts to queue the request via RabbitMQ. If that fails,
 * falls back to a direct API call.
 */
export const narRpaTrigger = async (
  payload: NarRpaTriggerPayload,
): Promise<RPATaskResponse> => {
  try {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }

    const rpaRequest = buildRpaTaskRequest(payload, token);
    const queueResponse = await sendRpaRequestsToRabbitMq(
      RPA_PATHS_ONLY_FOR_QUEUE.NAR_RPA_TRIGGER,
      rpaRequest,
    );

    if (queueResponse.success) {
      return queueResponse;
    }

    // Fallback to direct API call if queue fails
    return await sendDirectApiRequest(payload, token);
  } catch (error) {
    logDataToConsole("error in narRpaTrigger", error);
    throw error;
  }
};
